/**
 * Offline basemap prefetch: downloads basemap tiles for the Stuttgart region
 * straight into the service worker's runtime cache, so the map works offline
 * and doesn't reload tiles while zooming.
 *
 * Zoom levels use shrinking radii (full region coarse, city center detailed)
 * to keep the download reasonable (~1000 tiles ≈ 40–60 MB @2x):
 *   z6–z11 → 100 km, z12 → 60 km, z13 → 30 km, z14 → 15 km   (configurable)
 *
 * Both the URL *and* the cache name must match what the service worker uses
 * (vite.config.js runtimeCaching) and what cesium/viewer.js (BASEMAPS)
 * requests — otherwise Cesium's own requests miss the prefetched entries.
 * Hell und dunkel liegen bei verschiedenen Anbietern und damit in
 * verschiedenen Caches.
 */

const STORAGE_KEY = 'frm-tile-prefetch';

// MapTiler-Basiskarten (Style "Base", 256er-Endpoint). Die URLs müssen exakt
// denen in cesium/viewer.js (BASEMAPS) entsprechen — sonst greifen Prefetch
// und Cesiums eigene Requests an verschiedenen Cache-Einträgen vorbei. Beide
// Themes liegen im selben Cache 'maptiler-tiles' (gleicher Host, gleiche
// Service-Worker-Regel in vite.config.js).
const MAPTILER_KEY = import.meta.env.VITE_MAPTILER_KEY;
const maptilerTile = (style) => (z, x, y) =>
  `https://api.maptiler.com/maps/${style}/256/${z}/${x}/${y}.png?key=${MAPTILER_KEY}`;

const TILE_SOURCES = {
  light: { cache: 'maptiler-tiles', url: maptilerTile('base-v4') },
  dark: { cache: 'maptiler-tiles', url: maptilerTile('base-v4-dark') }
};

function lonToTileX(lon, zoom) {
  return Math.floor(((lon + 180) / 360) * 2 ** zoom);
}

function latToTileY(lat, zoom) {
  const rad = (lat * Math.PI) / 180;
  return Math.floor(
    ((1 - Math.log(Math.tan(rad) + 1 / Math.cos(rad)) / Math.PI) / 2) * 2 ** zoom
  );
}

/**
 * Tile-Koordinaten für einen Radius (km) um das Zentrum bei einem Zoom-Level.
 */
function tilesForRadius(center, radiusKm, zoom) {
  const dLat = radiusKm / 111.32;
  const dLon = radiusKm / (111.32 * Math.cos((center.lat * Math.PI) / 180));

  const x0 = lonToTileX(center.lon - dLon, zoom);
  const x1 = lonToTileX(center.lon + dLon, zoom);
  const y0 = latToTileY(center.lat + dLat, zoom); // Nord = kleineres y
  const y1 = latToTileY(center.lat - dLat, zoom);

  const max = 2 ** zoom - 1;
  const tiles = [];
  for (let x = Math.max(0, x0); x <= Math.min(max, x1); x++) {
    for (let y = Math.max(0, y0); y <= Math.min(max, y1); y++) {
      tiles.push([zoom, x, y]);
    }
  }
  return tiles;
}

function collectTiles(center, zoomRadii) {
  const tiles = [];
  for (const [zoom, radiusKm] of Object.entries(zoomRadii)) {
    tiles.push(...tilesForRadius(center, radiusKm, Number(zoom)));
  }
  return tiles;
}

/**
 * Anzahl der Kacheln für die konfigurierte Abdeckung (für die Anzeige).
 */
export function estimateTileCount(center, offlineConfig) {
  return collectTiles(center, offlineConfig.zoomRadii).length;
}

/**
 * Info über den letzten erfolgreichen Prefetch ({ at, theme, cached }) — null
 * wenn noch nie gelaufen.
 */
export function lastPrefetchInfo() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

/**
 * Lädt alle Kacheln der konfigurierten Abdeckung in den SW-Cache.
 * Bereits gecachte Kacheln werden übersprungen — ein zweiter Lauf ist billig.
 *
 * @returns {Promise<{cached: number, skipped: number, failed: number}>}
 */
export async function prefetchTiles({
  center,
  zoomRadii,
  theme = 'light',
  concurrency = 4,
  onProgress = null
}) {
  if (!('caches' in window)) {
    throw new Error('Cache-API nicht verfügbar');
  }

  const source = TILE_SOURCES[theme] || TILE_SOURCES.light;
  const buildUrl = source.url;
  const tiles = collectTiles(center, zoomRadii);
  const cache = await caches.open(source.cache);

  let done = 0;
  let cached = 0;
  let skipped = 0;
  let failed = 0;
  let index = 0;

  async function worker() {
    while (index < tiles.length) {
      const [z, x, y] = tiles[index++];
      const url = buildUrl(z, x, y);
      try {
        const existing = await cache.match(url);
        if (existing) {
          skipped++;
        } else {
          const response = await fetch(url);
          if (response.ok) {
            await cache.put(url, response);
            cached++;
          } else {
            failed++;
          }
        }
      } catch {
        failed++;
      }
      done++;
      if (onProgress && (done % 10 === 0 || done === tiles.length)) {
        onProgress(done, tiles.length);
      }
    }
  }

  const workers = [];
  for (let i = 0; i < Math.max(1, concurrency); i++) {
    workers.push(worker());
  }
  await Promise.all(workers);

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      at: Date.now(),
      theme,
      cached: cached + skipped
    }));
  } catch { /* storage voll — egal */ }

  return { cached: cached + skipped, skipped, failed };
}

/**
 * Automatischer Hintergrund-Prefetch: läuft frühestens alle `intervalDays`
 * Tage (bzw. nach Theme-Wechsel), wartet erst ein paar Sekunden, nutzt wenig
 * Parallelität und respektiert den Datenspar-Modus.
 */
export function schedulePrefetch({ center, zoomRadii, theme, intervalDays = 7 }) {
  if (!navigator.onLine) return;
  if (navigator.connection?.saveData) return;

  const info = lastPrefetchInfo();
  const ageMs = info ? Date.now() - info.at : Infinity;
  if (info && info.theme === theme && ageMs < intervalDays * 24 * 60 * 60 * 1000) {
    return;
  }

  setTimeout(() => {
    prefetchTiles({ center, zoomRadii, theme, concurrency: 2 })
      .then((result) => {
        console.log(
          `Offline tiles ready: ${result.cached} cached, ${result.failed} failed`
        );
      })
      .catch((error) => console.warn('Tile prefetch failed:', error.message));
  }, 12000); // App erst in Ruhe starten lassen
}
